import { useTranslation } from "react-i18next";
import { useFormat, type Formatters } from "../i18n/format";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import MoreIcon from "@mui/icons-material/MoreHorizOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageContainer, PageHeader } from "../components/PageHeader";
import AssetEditorDialog from "../components/distribution/AssetEditorDialog";
import AvailabilityChip from "../components/distribution/AvailabilityChip";
import SignagePlaylistDialog from "../components/SignagePlaylistDialog";
import { epgMappingStatus } from "@shared/epg-mapping";
import { saveErrorText } from "../components/distribution/findings";
import {
  foyerApi,
  type AssetSummary,
  type ChannelRow,
  type SignageScopeRef,
} from "../http/foyer";
import {
  MIN_SEARCH_LENGTH,
  formatMatchReasons,
  type AppliedSort,
} from "@shared/asset-search";
import { draftToCreate, draftToUpdate } from "@shared/asset-draft";
import type { Genre, Provider } from "@shared/types/content-api";
import type { SignagePlaylist } from "@shared/types/signage";
import { channelPosterUrl } from "@shared/signage";
import { imageSrc, resolveChannelLogoImage } from "@shared/vod-images";
import { tokens } from "../theme";
import ChannelMark from "../components/ChannelMark";
import { describeError, useToast } from "../components/toast";
import { PermissionGate } from "../auth/access";
import {
  AUTHOR_CHANNELS,
  AUTHOR_NENDA_CHANNELS,
} from "../components/distribution/catalogueGates";

/**
 * Channel management - a view over the Nenda Content API (`BE-15`).
 *
 * Every row here is a `type: "channel"` asset in a catalogue Foyer does **not**
 * own. Reads and writes go page → `foyerApi` → Foyer's routes → the `contentApi`
 * port → `nenda-content-api`; the browser never holds the credential, because
 * that one static key grants full control of the whole catalogue with no
 * tenancy of any kind.
 *
 * ## What left when the fixtures did
 *
 * - **The Published column.** There is no publish state upstream: an asset is
 *   live the instant it is POSTed. The column shows the thing that really gates
 *   delivery - the licence window - via `AvailabilityChip`.
 * - **Client-side sorting.** See below.
 *
 * ## The effect/live toggle is BACK (Sigge, 2026-09-05) - and it is not the
 * same toggle `BE-3` killed
 *
 * `BE-3` was right about the reason, not the conclusion: an effect channel is
 * a loop you build, and Foyer already has a first-class model for that -
 * `SignagePlaylist` - so Distribution must never grow a SECOND store for the
 * same thing. What made the old toggle a dead end was that it pointed at
 * nothing: the route surface was live channels only.
 *
 * It points at something real now. The toggle here does not add a model, it
 * adds a SECOND READ (and, for effect, the write): "Live" is the table above,
 * unchanged; "Effect" lists the exact same `SignagePlaylist`s
 * `/signage/channels` browses (`scope: "global"`), through
 * `listSignagePlaylistsIn`/`updateSignagePlaylistIn`/`deleteSignagePlaylistIn`
 * - never a Distribution-only copy of the data.
 *
 * **Editing and deleting moved here on purpose.** `/signage/channels` is the
 * CUSTOMER'S browse-and-schedule surface - it can never hold edit rights, or
 * "which channel can I edit" becomes a question of which premise's URL an
 * admin happens to be looking from. Distribution is Nenda-staff-only
 * (`canManageDistribution`), so authoring a channel that every customer sees
 * belongs here, same as everything else this page manages.
 *
 * `SignagePlaylistDialog` and `updateSignagePlaylistIn` already existed -
 * built for `BE-40`'s "New channel" button on the signage side, whose own
 * comment already said *"editing an existing channel belongs to
 * Distribution."* This is that promise, kept.
 *
 * ⚠️ New effect channels created from here set `effect: true` explicitly.
 * Nothing did before: the field is real (`SignagePlaylist.effect`, read by
 * `getsEffectChannels`' free-with-Signage entitlement) but the client never
 * sent it, so a channel authored through the old "New channel" flow was a
 * plain global playlist wearing a channel's clothes. `scope: "global"` is
 * still what makes a row show up as a "channel" at all - `effect` is the
 * narrower, additional fact that it is free venue ambience, not a signal to
 * gate the listing on.
 *
 * ## Two orders exist, and this list offers exactly those two
 *
 * The Content API has **no sorting at all**: order is FULLTEXT relevance while
 * a title is being searched, and `createdAt desc` otherwise. Anything else means
 * the server pages the complete filtered set before it can answer, and it
 * refuses past 2 000 rows rather than sorting a prefix.
 *
 * So the column headers are not sort controls. Sorting one PAGE client-side -
 * which is what a `TableSortLabel` over `rows` would do - sorts the seven rows
 * in hand and lies about the other nine thousand, and nothing on screen would
 * say so. The applied order is reported instead, from the server's own answer.
 */

/** How many rows arrive per page, and per press of "Show more". */
const PAGE_SIZE = 25;

/**
 * Effect-channel writes always land here, whichever premise an admin happens
 * to be looking from - see the module doc comment's `BE-40` reference.
 */
const EFFECT_SCOPE: SignageScopeRef = { kind: "global" };

/** What a cell shows when the row has nothing for it. */
const NONE = "—";

/**
 * A channel's mark on this page.
 *
 * The DRAWING moved to `components/ChannelMark` when the signage channel picker
 * needed the same thing and shipped its own broken version of it (a raw storage
 * path in an `<img>`, which renders as nothing). What stays here is the
 * RESOLUTION, because this page holds the raw `images` array while the signage
 * surfaces are handed a resolved url by their server projection.
 *
 * ⚠️ `resolveChannelLogoImage`, never `resolveVodThumbnailImage`: the latter
 * ranks `logo` LAST by design (a transparent mark reads as broken on a dark VOD
 * tile), so a channel WITH a logo drew its promo still instead. This file's
 * comment said the right thing and the call did the opposite for months.
 */
function ChannelRowMark({ channel }: { channel: ChannelRow }) {
  const image = resolveChannelLogoImage({
    images: channel.images ?? [],
    language: "sv",
    languages: ["sv", "en"],
  });
  return (
    <ChannelMark
      src={image ? imageSrc(image, { width: 104, height: 104 }) : null}
      name={channel.originalTitle || "?"}
      id={channel.assetId}
      size={52}
    />
  );
}

/** What the catalogue could actually be asked, said out loud. */
function SortCaption({ sort }: { sort: AppliedSort | null }) {
  const { t } = useTranslation("channels");
  if (!sort) return null;
  const order =
    sort.by === "relevance"
      ? t("channelsPage.sort.bestMatch")
      : sort.by === "created" && sort.order === "desc"
        ? t("channelsPage.sort.newestFirst")
        : t("channelsPage.sort.other", { by: sort.by, order: sort.order });
  return (
    <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
      {sort.applied === "complete-set" && sort.scanned !== undefined
        ? t("channelsPage.sort.completeSet", { order, count: sort.scanned })
        : t("channelsPage.sort.catalogueOrder", { order })}
    </Typography>
  );
}

function shortDate(fmt: Formatters, iso: string | undefined): string {
  if (!iso) return NONE;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? NONE : fmt.date(ms);
}

export default function ChannelsPage() {
  const toast = useToast();
  const { t } = useTranslation(["channels", "common"]);
  const fmt = useFormat();
  const [rows, setRows] = useState<ChannelRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [sort, setSort] = useState<AppliedSort | null>(null);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");

  const [providers, setProviders] = useState<Provider[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);

  const [dialog, setDialog] = useState<{ editing: ChannelRow | null } | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<{
    code?: string;
    message: string;
  } | null>(null);

  const [rowMenu, setRowMenu] = useState<{
    anchor: HTMLElement;
    channel: ChannelRow;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ChannelRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /**
   * `?open=<assetId>` opens that channel's editor.
   *
   * The Overview page's content-health list links here, so the problem it named
   * lands in the editor that fixes it rather than on a list the operator then
   * has to search. Read by id rather than found in the page in hand: the
   * channel is rarely on the first page. The parameter is dropped once acted
   * on, so closing the dialog does not reopen it.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("open");
  const [openError, setOpenError] = useState<string | null>(null);
  useEffect(() => {
    if (!openId) return;
    let alive = true;
    foyerApi
      .getDistributionChannel(openId)
      .then((row) => {
        if (!alive) return;
        setSaveError(null);
        setOpenError(null);
        setDialog({ editing: row });
      })
      .catch((e) => {
        if (!alive) return;
        const err = e as { code?: string; message: string };
        setOpenError(saveErrorText(t, err.code, err.message));
      })
      .finally(() => {
        if (!alive) return;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("open");
            return next;
          },
          { replace: true }
        );
      });
    return () => {
      alive = false;
    };
  }, [openId, setSearchParams, t]);

  /**
   * Live vs Effect - a second READ (and, for Effect, a write), not a second
   * model. See the module doc comment for why this is not `BE-3` again.
   */
  const [kind, setKind] = useState<"live" | "effect">("live");

  const [effectPlaylists, setEffectPlaylists] = useState<SignagePlaylist[]>([]);
  const [effectAssets, setEffectAssets] = useState<AssetSummary[]>([]);
  const [effectLoading, setEffectLoading] = useState(true);
  const [effectError, setEffectError] = useState<string | null>(null);
  const [effectTick, setEffectTick] = useState(0);

  const [effectDialog, setEffectDialog] = useState<{
    editing: SignagePlaylist | null;
  } | null>(null);
  const [effectSaving, setEffectSaving] = useState(false);
  // The thrown error, not a sentence - see `SignagePlaylistDialog.error`.
  const [effectSaveError, setEffectSaveError] = useState<unknown>(null);

  const [effectRowMenu, setEffectRowMenu] = useState<{
    anchor: HTMLElement;
    channel: SignagePlaylist;
  } | null>(null);
  const [effectConfirmDelete, setEffectConfirmDelete] =
    useState<SignagePlaylist | null>(null);
  const [effectDeleteError, setEffectDeleteError] = useState<string | null>(
    null
  );

  /**
   * The search is DEBOUNCED and only sent once it is long enough.
   *
   * Upstream refuses a search under three characters with bare plain text no
   * client can parse, and the FULLTEXT index behind the localised search does
   * not hold two-letter words at all - it would answer "nothing matches" rather
   * than "too short". Typing "TV" should not look like an empty catalogue.
   */
  useEffect(() => {
    const trimmed = query.trim();
    const next = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : "";
    const timer = setTimeout(() => setSearch(next), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const load = useCallback(
    async (nextPage: number, mode: "replace" | "append") => {
      if (mode === "replace") setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const answer = await foyerApi.listDistributionChannels({
          page: nextPage,
          pageSize: PAGE_SIZE,
          ...(search
            ? { search, sort: "relevance" as const, order: "desc" as const }
            : {}),
        });
        setRows((prev) =>
          mode === "append" ? [...prev, ...answer.channels] : answer.channels
        );
        setTotal(answer.pagination.total);
        setHasMore(answer.pagination.next !== null);
        setPage(nextPage);
        setSort(answer.sort);
      } catch (e) {
        const err = e as { code?: string; message: string };
        // Never an empty list on failure: "the catalogue is down" must not read
        // as "there are no channels".
        setError(saveErrorText(t, err.code, err.message));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [search, t]
  );

  useEffect(() => {
    void load(1, "replace");
  }, [load]);

  useEffect(() => {
    // Reference data, once. A provider typed by hand is a 400 after the fact;
    // a picker could only ever have offered real ids.
    foyerApi
      .listContentProviders()
      .then(setProviders)
      .catch(() => {});
    foyerApi
      .listContentGenres()
      .then(setGenres)
      .catch(() => {});
  }, []);

  async function submitChannel(
    ...[draft, options]: Parameters<
      React.ComponentProps<typeof AssetEditorDialog>["onSubmit"]
    >
  ): Promise<boolean> {
    const editing = dialog?.editing ?? null;
    setSaving(true);
    setSaveError(null);
    try {
      if (editing) {
        await foyerApi.updateDistributionChannel(
          editing.assetId,
          draftToUpdate(draft, editing, options)
        );
      } else {
        await foyerApi.createDistributionChannel(draftToCreate(draft));
      }
      // Re-read rather than patching the row in place: the server derives
      // `availability`, `rightsIssues` and `imageIssues` on every read, and a
      // locally-merged row would carry the verdicts of the payload we sent
      // rather than of the row that now exists.
      await load(1, "replace");
      toast.success(
        editing
          ? t("channelsPage.channelUpdated", {
              name: draft.originalTitle || editing.originalTitle,
            })
          : t("channelsPage.channelCreated", { name: draft.originalTitle })
      );
      // The editor slides out, then its onClose clears `dialog`.
      return true;
    } catch (e) {
      const err = e as { code?: string; message: string };
      setSaveError({ code: err.code, message: err.message });
      const channelName = draft.originalTitle || editing?.originalTitle;
      toast.error(
        channelName
          ? describeError(t, "saveNamedChannel", e, { name: channelName })
          : describeError(t, "saveChannel", e)
      );
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function removeChannel(channel: ChannelRow) {
    setDeleteError(null);
    try {
      await foyerApi.deleteDistributionChannel(channel.assetId);
      setConfirmDelete(null);
      toast.success(
        t("channelsPage.channelDeleted", { name: channel.originalTitle })
      );
      await load(1, "replace");
    } catch (e) {
      const err = e as { code?: string; message: string };
      setDeleteError(saveErrorText(t, err.code, err.message));
      toast.error(
        describeError(t, "deleteChannel", e, { name: channel.originalTitle })
      );
    }
  }

  /**
   * Writes always land at `{kind: "global"}`, whichever record is open - a
   * Nenda admin authoring here is editing the platform's own catalogue, not
   * anything scoped to a premise or company. Mirrors `SignageChannelsContainer`
   * before `authoring` moved off it.
   */
  useEffect(() => {
    if (kind !== "effect") return;
    let alive = true;
    setEffectLoading(true);
    foyerApi
      .listSignagePlaylistsIn(EFFECT_SCOPE)
      .then((list) => {
        if (!alive) return;
        setEffectPlaylists(list.filter((p) => p.scope === "global"));
        setEffectError(null);
      })
      .catch((e) => {
        if (!alive) return;
        const err = e as { code?: string; message: string };
        setEffectError(saveErrorText(t, err.code, err.message));
      })
      .finally(() => alive && setEffectLoading(false));
    return () => {
      alive = false;
    };
  }, [kind, effectTick, t]);

  useEffect(() => {
    // The global media library only, same reasoning as the signage side: a
    // channel is built from Nenda's own files, never a customer's uploads.
    if (kind !== "effect") return;
    let alive = true;
    foyerApi
      .listAssets({ kind: "global" })
      .then((list) => alive && setEffectAssets(list))
      .catch(() => alive && setEffectAssets([]));
    return () => {
      alive = false;
    };
  }, [kind]);

  async function saveEffectChannel(
    patch: Partial<SignagePlaylist>
  ): Promise<boolean> {
    const editing = effectDialog?.editing ?? null;
    setEffectSaving(true);
    setEffectSaveError(null);
    try {
      if (editing) {
        await foyerApi.updateSignagePlaylistIn(EFFECT_SCOPE, editing.id, patch);
      } else {
        // ⚠️ `effect: true` explicitly - see the module doc comment. Nothing
        // upstream of this call ever sent it, so a channel minted here without
        // it would be a plain global playlist that only LOOKS like a channel.
        await foyerApi.createSignagePlaylistIn(EFFECT_SCOPE, {
          ...patch,
          effect: true,
        });
      }
      setEffectTick((t) => t + 1);
      const createdName = patch.name ?? "";
      toast.success(
        editing
          ? t("channelsPage.effectUpdated", {
              name: patch.name ?? editing.name,
            })
          : createdName
            ? t("channelsPage.effectCreated", { name: createdName })
            : t("channelsPage.effectCreatedUnnamed")
      );
      // The editor slides out, then its onClose clears `effectDialog`.
      return true;
    } catch (e) {
      setEffectSaveError(e);
      const effectName = (patch.name ?? editing?.name ?? "").trim();
      toast.error(
        effectName
          ? describeError(t, "saveNamedEffectChannel", e, { name: effectName })
          : describeError(t, "saveEffectChannel", e)
      );
      return false;
    } finally {
      setEffectSaving(false);
    }
  }

  async function removeEffectChannel(channel: SignagePlaylist) {
    setEffectDeleteError(null);
    try {
      await foyerApi.deleteSignagePlaylistIn(EFFECT_SCOPE, channel.id);
      setEffectConfirmDelete(null);
      setEffectTick((t) => t + 1);
      toast.success(t("channelsPage.effectDeleted", { name: channel.name }));
    } catch (e) {
      const err = e as { code?: string; message: string };
      setEffectDeleteError(saveErrorText(t, err.code, err.message));
      toast.error(
        describeError(t, "deleteEffectChannel", e, { name: channel.name })
      );
    }
  }

  const effectCategories = [
    ...new Set(
      effectPlaylists.map((p) => p.category?.trim() ?? "").filter(Boolean)
    ),
  ];

  const filteredEffectPlaylists = query.trim()
    ? effectPlaylists.filter((p) =>
        p.name.toLowerCase().includes(query.trim().toLowerCase())
      )
    : effectPlaylists;

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("channelsPage.eyebrow")}
        title={t("channelsPage.title")}
        subtitle={
          kind === "live"
            ? t("channelsPage.subtitleLive")
            : t("channelsPage.subtitleEffect")
        }
      />

      <ToggleButtonGroup
        exclusive
        size="small"
        value={kind}
        onChange={(_e, next) => next && setKind(next)}
        sx={{ mb: 2 }}
        data-testid="channels-kind-toggle"
      >
        <ToggleButton value="live" data-testid="channels-kind-live">
          {t("channelsPage.liveChannels")}
        </ToggleButton>
        <ToggleButton value="effect" data-testid="channels-kind-effect">
          {t("channelsPage.effectChannels")}
        </ToggleButton>
      </ToggleButtonGroup>

      <Card sx={{ p: 3 }}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          alignItems={{ lg: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack spacing={0.5}>
            <Typography
              variant="body2"
              sx={{ fontWeight: "fontWeightBold", color: tokens.inkMuted }}
              data-testid="channels-count"
            >
              {kind === "live"
                ? loading
                  ? t("channelsPage.loading")
                  : t("channelsPage.countOfTotal", {
                      shown: rows.length,
                      total,
                    })
                : effectLoading
                  ? t("channelsPage.loading")
                  : t("channelsPage.countOfTotal", {
                      shown: filteredEffectPlaylists.length,
                      total: effectPlaylists.length,
                    })}
            </Typography>
            {kind === "live" && <SortCaption sort={sort} />}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={2}>
            <TextField
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                kind === "live"
                  ? t("channelsPage.searchLive")
                  : t("channelsPage.searchEffect")
              }
              data-testid="channel-search"
              helperText={
                kind === "live" &&
                query.trim().length > 0 &&
                query.trim().length < MIN_SEARCH_LENGTH
                  ? t("channelsPage.minChars", { count: MIN_SEARCH_LENGTH })
                  : " "
              }
              sx={{ width: { xs: "100%", sm: 360 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <PermissionGate
              {...(kind === "live" ? AUTHOR_CHANNELS : AUTHOR_NENDA_CHANNELS)}
            >
              <Button
                variant="contained"
                onClick={() => {
                  if (kind === "live") {
                    setSaveError(null);
                    setDialog({ editing: null });
                  } else {
                    setEffectSaveError(null);
                    setEffectDialog({ editing: null });
                  }
                }}
                data-testid="channel-create"
                sx={{ whiteSpace: "nowrap" }}
              >
                {kind === "live"
                  ? t("channelsPage.createChannel")
                  : t("channelsPage.newEffectChannel")}
              </Button>
            </PermissionGate>
          </Stack>
        </Stack>

        {kind === "live" && error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            data-testid="channels-error"
            action={
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => void load(1, "replace")}
              >
                {t("channelsPage.retry")}
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {openError && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            onClose={() => setOpenError(null)}
            data-testid="channels-open-error"
          >
            {t("channelsPage.couldNotOpen", { reason: openError })}
          </Alert>
        )}

        {kind === "effect" && effectError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            data-testid="channels-error"
            action={
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => setEffectTick((n) => n + 1)}
              >
                {t("channelsPage.retry")}
              </Button>
            }
          >
            {effectError}
          </Alert>
        )}

        {kind === "live" ? (
          <TableContainer
            sx={{ border: `1px solid ${tokens.divider}`, borderRadius: 2 }}
          >
            <Table data-testid="channels-table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 280 }}>
                    {t("common:labels.name")}
                  </TableCell>
                  <TableCell sx={{ width: 180 }}>
                    {t("common:labels.provider")}
                  </TableCell>
                  <TableCell sx={{ width: 170 }}>
                    {t("channelsPage.columns.availability")}
                  </TableCell>
                  <TableCell sx={{ width: 130 }}>
                    {t("common:labels.created")}
                  </TableCell>
                  <TableCell sx={{ width: 130 }}>
                    {t("common:labels.changed")}
                  </TableCell>
                  <TableCell sx={{ width: 64 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Stack alignItems="center" sx={{ py: 5 }}>
                        <CircularProgress size={28} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}

                {!loading &&
                  rows.map((row) => {
                    const caption = row.matches
                      ? formatMatchReasons(row.matches, ["originalTitle"])
                      : null;
                    return (
                      <TableRow
                        key={row.assetId}
                        hover
                        data-testid={`channel-row-${row.assetId}`}
                        sx={{ height: 88, cursor: "pointer" }}
                        // The row IS the asset, so opening it is the whole
                        // detail view - same target the row menu's Edit hits.
                        // Reaching it only through a ⋯ menu made the details of
                        // a channel look like they were not there at all.
                        tabIndex={0}
                        onClick={() => {
                          setSaveError(null);
                          setDialog({ editing: row });
                        }}
                        onKeyDown={(e) => {
                          // Guarded on the row itself: the actions button inside
                          // fires its own Enter, and that keydown bubbles here.
                          if (e.key !== "Enter" || e.target !== e.currentTarget)
                            return;
                          e.preventDefault();
                          setSaveError(null);
                          setDialog({ editing: row });
                        }}
                      >
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <ChannelRowMark channel={row} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="bodySm"
                                sx={{
                                  fontWeight: "fontWeightSemibold",
                                  color: tokens.ink,
                                }}
                              >
                                {row.originalTitle || t("channelsPage.noName")}
                              </Typography>
                              {caption && (
                                <Typography
                                  variant="micro"
                                  sx={{ color: tokens.inkMuted }}
                                  data-testid={`channel-match-${row.assetId}`}
                                >
                                  {caption}
                                </Typography>
                              )}
                              <Typography
                                variant="micro"
                                sx={{
                                  color: tokens.inkMuted,
                                  fontFamily: "fontFamilyMono",
                                  mt: 0.5,
                                }}
                              >
                                {row.assetId}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="meta">
                            {row.provider?.name || NONE}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <AvailabilityChip availability={row.availability} />
                            {/* An unmapped channel gets NO programme guide, ever,
                              and today the only trace is a log line in a
                              service whose structured reports are off (`BE-22`).
                              Drawn only when the field was actually loaded:
                              `unknown` stays silent, because a warning on a
                              channel that is fine teaches people to ignore the
                              warning. */}
                            {epgMappingStatus(row) === "unmapped" && (
                              <Chip
                                size="small"
                                color="warning"
                                variant="outlined"
                                label={t("channelsPage.noGuide")}
                                data-testid={`channel-no-epg-${row.assetId}`}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="meta">
                            {shortDate(fmt, row.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="meta">
                            {shortDate(fmt, row.updatedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <PermissionGate {...AUTHOR_CHANNELS}>
                            <IconButton
                              size="small"
                              aria-label={t("channelsPage.actionsFor", {
                                name: row.originalTitle,
                              })}
                              data-testid={`channel-menu-${row.assetId}`}
                              onClick={(e) =>
                                setRowMenu({
                                  anchor: e.currentTarget,
                                  channel: row,
                                })
                              }
                            >
                              <MoreIcon />
                            </IconButton>
                          </PermissionGate>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                {!loading && rows.length === 0 && !error && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography
                        variant="body2"
                        sx={{
                          py: 4,
                          textAlign: "center",
                          color: tokens.inkMuted,
                        }}
                      >
                        {search
                          ? t("channelsPage.noMatch", { query: search })
                          : t("channelsPage.noChannels")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Divider />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 2.5 }}
            >
              <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
                {t("channelsPage.shownOfTotal", { shown: rows.length, total })}
              </Typography>
              {hasMore && (
                <Button
                  endIcon={
                    loadingMore ? (
                      <CircularProgress size={14} />
                    ) : (
                      <ExpandMoreIcon />
                    )
                  }
                  disabled={loadingMore}
                  onClick={() => void load(page + 1, "append")}
                  data-testid="channels-show-more"
                  sx={{
                    fontSize: 14,
                    fontWeight: "fontWeightBold",
                    color: tokens.inkMuted,
                  }}
                >
                  {t("channelsPage.showMore")}
                </Button>
              )}
            </Stack>
          </TableContainer>
        ) : (
          <TableContainer
            sx={{ border: `1px solid ${tokens.divider}`, borderRadius: 2 }}
          >
            <Table data-testid="effect-channels-table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 280 }}>
                    {t("common:labels.name")}
                  </TableCell>
                  <TableCell sx={{ width: 200 }}>
                    {t("common:labels.category")}
                  </TableCell>
                  <TableCell sx={{ width: 120 }}>
                    {t("channelsPage.columns.items")}
                  </TableCell>
                  <TableCell sx={{ width: 100 }}>
                    {t("channelsPage.columns.sound")}
                  </TableCell>
                  <TableCell sx={{ width: 64 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {effectLoading && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Stack alignItems="center" sx={{ py: 5 }}>
                        <CircularProgress size={28} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}

                {!effectLoading &&
                  filteredEffectPlaylists.map((p) => (
                    <TableRow
                      key={p.id}
                      hover
                      data-testid={`effect-channel-row-${p.id}`}
                      sx={{ height: 72, cursor: "pointer" }}
                      tabIndex={0}
                      onClick={() => {
                        setEffectSaveError(null);
                        setEffectDialog({ editing: p });
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter" || e.target !== e.currentTarget)
                          return;
                        e.preventDefault();
                        setEffectSaveError(null);
                        setEffectDialog({ editing: p });
                      }}
                    >
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: "8px",
                              flexShrink: 0,
                              bgcolor: tokens.rowAlt,
                              backgroundImage: channelPosterUrl(p)
                                ? `url(${channelPosterUrl(p)})`
                                : undefined,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }}
                          />
                          <Typography
                            variant="bodySm"
                            sx={{
                              fontWeight: "fontWeightSemibold",
                              color: tokens.ink,
                            }}
                          >
                            {p.name || t("channelsPage.noName")}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="meta">
                          {p.category?.trim() ||
                            t("channelsPage.uncategorised")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="meta">{p.items.length}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="meta">
                          {p.soundEnabled
                            ? t("common:labels.on")
                            : t("common:labels.off")}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <PermissionGate {...AUTHOR_NENDA_CHANNELS}>
                          <IconButton
                            size="small"
                            aria-label={t("channelsPage.actionsFor", {
                              name: p.name,
                            })}
                            data-testid={`effect-channel-menu-${p.id}`}
                            onClick={(e) =>
                              setEffectRowMenu({
                                anchor: e.currentTarget,
                                channel: p,
                              })
                            }
                          >
                            <MoreIcon />
                          </IconButton>
                        </PermissionGate>
                      </TableCell>
                    </TableRow>
                  ))}

                {!effectLoading &&
                  filteredEffectPlaylists.length === 0 &&
                  !effectError && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography
                          variant="body2"
                          sx={{
                            py: 4,
                            textAlign: "center",
                            color: tokens.inkMuted,
                          }}
                        >
                          {query.trim()
                            ? t("channelsPage.noEffectMatch", {
                                query: query.trim(),
                              })
                            : t("channelsPage.noEffectChannels")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
              </TableBody>
            </Table>
            <Divider />
            <Stack sx={{ px: 2, py: 2.5 }}>
              <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
                {t("channelsPage.shownOfTotal", {
                  shown: filteredEffectPlaylists.length,
                  total: effectPlaylists.length,
                })}
              </Typography>
            </Stack>
          </TableContainer>
        )}
      </Card>

      <Menu
        open={Boolean(rowMenu)}
        anchorEl={rowMenu?.anchor ?? null}
        onClose={() => setRowMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (rowMenu) {
              setSaveError(null);
              setDialog({ editing: rowMenu.channel });
            }
            setRowMenu(null);
          }}
          data-testid="channel-edit"
        >
          <EditIcon fontSize="small" sx={{ mr: 1.5 }} />
          {t("common:buttons.edit")}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (rowMenu) {
              setDeleteError(null);
              setConfirmDelete(rowMenu.channel);
            }
            setRowMenu(null);
          }}
          sx={{ color: tokens.error }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} />
          {t("common:buttons.delete")}
        </MenuItem>
      </Menu>

      {/* Deleting is refused while a location still carries the channel, and the
          registry behind that check fails CLOSED - "I do not know" must never
          read as "nobody". So this confirms, and then reports what came back. */}
      <Dialog
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
      >
        <DialogTitle>{t("channelsPage.deleteChannelTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("channelsPage.deleteChannelBody", {
              name: confirmDelete?.originalTitle,
            })}
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="delete-error">
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>
            {t("common:buttons.cancel")}
          </Button>
          <PermissionGate {...AUTHOR_CHANNELS} deniedProps={{ disabled: true }}>
            <Button
              color="error"
              variant="contained"
              data-testid="channel-delete-confirm"
              onClick={() => confirmDelete && void removeChannel(confirmDelete)}
            >
              {t("common:buttons.delete")}
            </Button>
          </PermissionGate>
        </DialogActions>
      </Dialog>

      {/* Keyed so opening a different channel REMOUNTS rather than trying to
          copy new props into old state. The key changes on every target, so the
          dialog seeds itself once and never watches for a change - which
          matters MORE now that the row arrives asynchronously. */}
      {dialog && (
        <AssetEditorDialog
          key={dialog.editing?.assetId ?? "new"}
          open
          kind="channel"
          initial={dialog.editing}
          providers={providers}
          genres={genres}
          saving={saving}
          error={saveError}
          onClose={() => setDialog(null)}
          onSubmit={submitChannel}
        />
      )}

      <Menu
        open={Boolean(effectRowMenu)}
        anchorEl={effectRowMenu?.anchor ?? null}
        onClose={() => setEffectRowMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (effectRowMenu) {
              setEffectSaveError(null);
              setEffectDialog({ editing: effectRowMenu.channel });
            }
            setEffectRowMenu(null);
          }}
          data-testid="effect-channel-edit"
        >
          <EditIcon fontSize="small" sx={{ mr: 1.5 }} />
          {t("common:buttons.edit")}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (effectRowMenu) {
              setEffectDeleteError(null);
              setEffectConfirmDelete(effectRowMenu.channel);
            }
            setEffectRowMenu(null);
          }}
          sx={{ color: tokens.error }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} />
          {t("common:buttons.delete")}
        </MenuItem>
      </Menu>

      {/* Every customer who can schedule this channel loses it the moment it
          is gone, with no confirmation on their side - so this one asks
          plainly rather than treating an effect channel as disposable because
          nothing plays it directly. */}
      <Dialog
        open={effectConfirmDelete !== null}
        onClose={() => setEffectConfirmDelete(null)}
      >
        <DialogTitle>{t("channelsPage.deleteEffectTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("channelsPage.deleteEffectBody", {
              name: effectConfirmDelete?.name,
            })}
          </Typography>
          {effectDeleteError && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              data-testid="effect-channel-delete-error"
            >
              {effectDeleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEffectConfirmDelete(null)}>
            {t("common:buttons.cancel")}
          </Button>
          <PermissionGate
            {...AUTHOR_NENDA_CHANNELS}
            deniedProps={{ disabled: true }}
          >
            <Button
              color="error"
              variant="contained"
              data-testid="effect-channel-delete-confirm"
              onClick={() =>
                effectConfirmDelete &&
                void removeEffectChannel(effectConfirmDelete)
              }
            >
              {t("common:buttons.delete")}
            </Button>
          </PermissionGate>
        </DialogActions>
      </Dialog>

      {/* Same editor `/signage/channels` used to open for a NEW channel only
          (`BE-40`) - now also opened here, keyed by opening, for an existing
          one too. See the module doc comment for why editing lives here and
          not there. */}
      {effectDialog && (
        <SignagePlaylistDialog
          key={effectDialog.editing?.id ?? "new"}
          open
          onClose={() => setEffectDialog(null)}
          playlist={effectDialog.editing}
          assets={effectAssets}
          onSave={saveEffectChannel}
          saving={effectSaving}
          error={effectSaveError}
          asChannel
          scope={EFFECT_SCOPE}
          categories={effectCategories}
        />
      )}
    </PageContainer>
  );
}
